import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { SEVERITY, formatValidationSummary } from '../utils/snapshotValidation';

const severityConfig = {
  [SEVERITY?.ERROR]: {
    bg: 'bg-red-50 dark:bg-red-950/30',
    border: 'border-red-200 dark:border-red-800/50',
    icon: 'AlertCircle',
    iconColor: 'text-red-600',
    titleColor: 'text-red-700 dark:text-red-400',
    badgeBg: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
    label: 'Validation Errors',
  },
  [SEVERITY?.WARNING]: {
    bg: 'bg-amber-50 dark:bg-amber-950/30',
    border: 'border-amber-200 dark:border-amber-800/50',
    icon: 'AlertTriangle',
    iconColor: 'text-amber-600',
    titleColor: 'text-amber-700 dark:text-amber-400',
    badgeBg: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
    label: 'Data Warnings',
  },
  [SEVERITY?.INFO]: {
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    border: 'border-blue-200 dark:border-blue-800/50',
    icon: 'Info',
    iconColor: 'text-blue-600',
    titleColor: 'text-blue-700 dark:text-blue-400',
    badgeBg: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
    label: 'Data Notes',
  },
  [SEVERITY?.OK]: {
    bg: 'bg-emerald-50 dark:bg-emerald-950/30',
    border: 'border-emerald-200 dark:border-emerald-800/50',
    icon: 'CheckCircle',
    iconColor: 'text-emerald-600',
    titleColor: 'text-emerald-700 dark:text-emerald-400',
    badgeBg: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
    label: 'All Checks Passed',
  },
};

const IssueLine = ({ item }) => {
  const dotColor =
    item?.severity === SEVERITY?.ERROR
      ? 'bg-red-500'
      : item?.severity === SEVERITY?.WARNING
      ? 'bg-amber-400' :'bg-blue-400';

  return (
    <div className="flex items-start gap-2 py-1">
      <div className={`w-1.5 h-1.5 rounded-full ${dotColor} mt-1.5 flex-shrink-0`} />
      <span className="text-xs text-foreground/80 leading-snug">{item?.message}</span>
    </div>
  );
};

/**
 * ValidationBanner
 *
 * Props:
 *   result  — output of validateMonthlyKPIs() or validateYearlyKPIs()
 *   section — label string, e.g. "Monthly" or "Yearly"
 *   compact — if true, only show when there are errors/warnings (hide OK state)
 */
const ValidationBanner = ({ result, section = '', compact = false }) => {
  const [expanded, setExpanded] = useState(false);

  if (!result) return null;

  // In compact mode, hide the banner when everything is OK
  if (compact && result?.severity === SEVERITY?.OK) return null;

  const cfg = severityConfig?.[result?.severity] || severityConfig?.[SEVERITY?.OK];
  const summary = formatValidationSummary(result);

  const totalIssues = summary?.total ?? 0;
  const isOK = result?.severity === SEVERITY?.OK;

  return (
    <div className={`${cfg?.bg} ${cfg?.border} border rounded-xl overflow-hidden transition-all duration-200`}>
      {/* Header row */}
      <button
        type="button"
        onClick={() => !isOK && setExpanded(prev => !prev)}
        className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left ${!isOK ? 'cursor-pointer hover:bg-black/5 dark:hover:bg-white/5' : 'cursor-default'}`}
      >
        <Icon name={cfg?.icon} size={14} className={cfg?.iconColor} />
        <span className={`text-xs font-semibold flex-1 ${cfg?.titleColor}`}>
          {section ? `${section} — ` : ''}{cfg?.label}
        </span>
        {!isOK && totalIssues > 0 && (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cfg?.badgeBg}`}>
            {totalIssues} issue{totalIssues !== 1 ? 's' : ''}
          </span>
        )}
        {!isOK && (
          <Icon
            name={expanded ? 'ChevronUp' : 'ChevronDown'}
            size={13}
            className={cfg?.iconColor}
          />
        )}
      </button>

      {/* Expanded issue list */}
      {!isOK && expanded && summary && (
        <div className="px-4 pb-3 border-t border-current/10 space-y-0.5">
          {summary?.errors?.length > 0 && (
            <div className="pt-2">
              <p className="text-[10px] font-bold text-red-600 uppercase tracking-wide mb-1">Errors</p>
              {summary?.errors?.map((item, i) => <IssueLine key={i} item={item} />)}
            </div>
          )}
          {summary?.warnings?.length > 0 && (
            <div className="pt-2">
              <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wide mb-1">Warnings</p>
              {summary?.warnings?.map((item, i) => <IssueLine key={i} item={item} />)}
            </div>
          )}
          {summary?.infos?.length > 0 && (
            <div className="pt-2">
              <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wide mb-1">Notes</p>
              {summary?.infos?.map((item, i) => <IssueLine key={i} item={item} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ValidationBanner;
